/**
 * PartialShipmentsTab.js
 *
 * Shared table for partial-shipment views (e.g. Drop Trailers).
 * Uses the same column manager, pinning, resize, reorder, and cell-expand
 * behavior as ShipmentsTable.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import httpClient from "../../../../api/httpClient";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import useExpandedRowHover from "../../../../components/ACS-Dashboard/ACS-Blocks/useExpandedRowHover";
import { MoreHorizontal } from "lucide-react";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import {
  addTab,
  setListViewState,
  setStatusFilter,
  setAdvancedFilters,
} from "../../../../redux/slices/shipmentSlice";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import { SORTABLE_COLUMNS, FILTER_TABS } from "./Utils/sortableColumns";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { usePrivilege } from "../../../../guard/usePrivilege";
import { refreshRatesAndUpdateCharges } from "./ViewShipment/rateRefresh";
import { useShipmentStatuses } from "./Utils/shipmentStatuses";

import {
  TABLE_HEADERS,
  DISABLED_STATUSES,
  makeKey,
  buildShipmentSearchPayload,
  formatShipmentRow,
  useShipmentFilters,
  AdvancedFilterToolbar,
  AdvancedFilterModal,
  DEFAULT_ADVANCED_FILTERS,
  DEFAULT_QUICK_FILTER,
  filtersOverrideStatus,
  isRefreshBlockedForShipment,

} from "./shipmentShared";
import { useTableSavedViews, TableSavedViewsControl } from "../../../TableSavedViews";

// ─── Layout constants (aligned with ShipmentsTable) ───────────────────────────

const DATA_COLUMN_MIN_WIDTH    = 200;
const DATA_COLUMN_MAX_WIDTH    = 420;
const SELECTION_COLUMN_WIDTH   = 80;
const ACTION_COLUMN_WIDTH      = 50;
const SHIPMENT_NUMBER_HEADER   = "Shipment Number";

// ─── Main Component ───────────────────────────────────────────────────────────

const PartialShipmentsTab = ({
  config,
  searchQuery,
  filter,
  searchTrigger,
  setTotalShipments,
  userId,
  stateKey              = "partial-shipments",
  enableAdvancedFilters   = false,
}) => {
  const { moduleName, apiEndpoint, paginationLabel, filterType = FILTER_TABS.ALL, defaultPageSize = 50 } = config;

  const dispatch = useDispatch();
  const { listViewState = {}, advancedFilters: advancedFiltersByTab = {} } = useSelector(
    (s) => s.shipments
  );
  const savedViewState = listViewState[stateKey] || {};
  // What the store knew about this tab as this mount began: where the user left it
  // earlier in the session, before anything here writes to it.
  const sessionStateOnMountRef = useRef(savedViewState);
  // Likewise for its rule-based filters, which live in their own slice of state and
  // are not part of savedViewState.
  const sessionFiltersOnMountRef = useRef(advancedFiltersByTab[stateKey]);

  const [shipments,          setShipments]         = useState([]);
  const [isLoading,          setIsLoading]          = useState(false);
  const [selectedShipments,  setSelectedShipments]  = useState([]);
  const [currentPage,        setCurrentPage]        = useState(savedViewState.currentPage ?? 1);
  const [shipmentsPerPage,   setShipmentsPerPage]   = useState(savedViewState.shipmentsPerPage ?? defaultPageSize);
  const [openDropdownRow,    setOpenDropdownRow]    = useState(null);
  const [dropdownPos,        setDropdownPos]        = useState({ top: 0, left: 0 });
  const [totalPages,         setTotalPages]         = useState(1);
  // Kept here as well as reported upward: the pagination bar shows which rows the
  // page covers, which needs the count and not just the number of pages.
  const [totalItems,         setTotalItems]         = useState(0);
  const [showFilterModal,    setShowFilterModal]    = useState(false);
  const [showBulkMenu,       setShowBulkMenu]       = useState(false);
  const [isRefreshingMiles,  setIsRefreshingMiles]  = useState(false);
  const [isRefreshingRates,  setIsRefreshingRates]  = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  // Hovering any cell wraps the whole row, so long values in every column are readable at once.
  const { expandedRowKey, getRowHoverProps } = useExpandedRowHover();

  const [sortBy,    setSortBy]    = useState(savedViewState.sortBy    || null);
  const [ascending, setAscending] = useState(savedViewState.ascending ?? false);

  const dropdownRef       = useRef(null);
  const checkboxHeaderRef = useRef(null);
  const bulkMenuRef       = useRef(null);
  const tableContainerRef = useRef(null);

  const prevFilterRef     = useRef(filter);
  const prevSearchTrigRef = useRef(searchTrigger);

  const token      = Cookies.get("authToken");
  const officeCode = Cookies.get("officeCodes");

  const { hasPrivilege } = usePrivilege();
  // Refresh Miles/Refresh Rates: Shipment Admin only -- not every shipper user, and no
  // carrier user regardless of privilege. isAdminUser (shipments:ADMIN_VIEW) alone isn't
  // org-scoped, so isShipperOrg is what excludes carriers here -- same combined-gate pattern
  // ShipmentTable.js uses for the same two actions on the full Shipment List.
  const isAdminUser = hasPrivilege("shipments:ADMIN_VIEW");

  // LGX-1164: same carrier-aware status list as the main Shipment List's Advanced Filter.
  const { apiStatuses: rawStatusOptions } = useShipmentStatuses();
  const advancedFilterStatusOptions = rawStatusOptions.map((s) => s.toUpperCase());
  let isShipperOrg = false;
  try {
    const storedOrgTypes = JSON.parse(localStorage.getItem("organizationTypes") || "[]");
    const normalizedOrgTypes = (Array.isArray(storedOrgTypes) ? storedOrgTypes : [])
      .map((t) => (t ?? "").toString().toLowerCase().trim())
      .filter(Boolean);
    isShipperOrg = normalizedOrgTypes.length === 1 && normalizedOrgTypes[0] === "shipper";
  } catch {
    isShipperOrg = false;
  }
  const canRefreshMilesOrRates = isAdminUser && isShipperOrg;

  // Positions the bulk menu relative to the table's own scroll container (same as
  // ShipmentsTable.js's getBulkMenuStyle) -- NOT window.scrollY, which double-counts scroll
  // offset once the grid's own internal scroll container has moved and places the menu far
  // below the actual checkbox.
  const getBulkMenuStyle = () => {
    const container = tableContainerRef.current;
    const header = checkboxHeaderRef.current;
    if (!container || !header) return null;

    const checkbox = header.querySelector('input[type="checkbox"]');
    const anchor = checkbox ?? header;
    const anchorRect = anchor.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return {
      top:
        anchorRect.bottom - containerRect.top + container.scrollTop + 6,
      // Left-align on the checkbox itself (not centered on its narrow width) -- centering put
      // the menu's left half directly over the checkbox, looking like it overlapped it.
      left:
        anchorRect.left -
        containerRect.left +
        container.scrollLeft,
    };
  };


  const { filters: advancedFilters, updateFilters, clearFilters, removePill, activeCount } =
    useShipmentFilters({ stateKey, persist: enableAdvancedFilters });

  const prevQuickFilterForDirtyRef = useRef(filter);
  const prevAdvancedFiltersForDirtyRef = useRef(JSON.stringify(advancedFilters));

  const tableViews = useTableSavedViews({
    pageKey: stateKey,
    allHeaders: TABLE_HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", SHIPMENT_NUMBER_HEADER],
    getExtraConfig: () => ({
      sortBy,
      ascending,
      filter,
      advancedFilters: enableAdvancedFilters ? advancedFilters : undefined,
      currentPage,
      pageSize: shipmentsPerPage,
    }),
    onApplyConfig: (config, _viewMeta, options = {}) => {
      // Coming back to the module mid-session, the store already holds the page,
      // filter and sort this tab was left on, and the view that gets re-applied on
      // every mount — a default one, or just the last one used — would put the user
      // back where the view starts instead. A fresh login has no session state to
      // keep (logout clears the store), so there the view still owns the first load.
      //
      // Only what is actually there is protected: a config carrying rule-based
      // filters is applied unless the session has that tab's filters too. Otherwise
      // a partly restored session — the tab marked visited with its filters missing —
      // would block its own filters from ever being applied, and the tab would list
      // every shipment.
      const session = sessionStateOnMountRef.current;
      const sessionCoversFilters =
        config.advancedFilters === undefined ||
        sessionFiltersOnMountRef.current !== undefined;
      if (options.isInitialLoad && session?.visited && sessionCoversFilters) return;


      if (config.sortBy !== undefined) setSortBy(config.sortBy);
      if (config.ascending !== undefined) setAscending(config.ascending);
      if (config.currentPage) setCurrentPage(config.currentPage);
      if (config.pageSize) setShipmentsPerPage(config.pageSize);
      if (config.filter !== undefined) {
        dispatch(setStatusFilter(config.filter));
        dispatch(setListViewState({ stateKey, filter: config.filter }));
      }
      if (enableAdvancedFilters && config.advancedFilters !== undefined) {
        dispatch(setAdvancedFilters({
          stateKey,
          filters: config.advancedFilters ?? DEFAULT_ADVANCED_FILTERS,
        }));
      } else if (options.resetExtraConfig) {
        if (enableAdvancedFilters) {
          dispatch(setAdvancedFilters({ stateKey, filters: DEFAULT_ADVANCED_FILTERS }));
        }
        dispatch(setStatusFilter(DEFAULT_QUICK_FILTER));
        dispatch(setListViewState({ stateKey, filter: DEFAULT_QUICK_FILTER }));
      }
    },
  });

  const {
    columnWidths: savedColumnWidths,
    setColumnWidths: onColumnResize,
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    markDirty,
    reconcileDirtyState,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  useEffect(() => {
    if (!activeView || viewsLoading) return;

    const quickFilterChanged = prevQuickFilterForDirtyRef.current !== filter;
    const advancedFiltersJson = JSON.stringify(advancedFilters);
    const advancedFiltersChanged =
      prevAdvancedFiltersForDirtyRef.current !== advancedFiltersJson;

    prevQuickFilterForDirtyRef.current = filter;
    prevAdvancedFiltersForDirtyRef.current = advancedFiltersJson;

    if (!quickFilterChanged && !advancedFiltersChanged) return;

    reconcileDirtyState();
  }, [activeView, filter, advancedFilters, viewsLoading, reconcileDirtyState]);

  useEffect(() => {
    if (!isLoading) { setShowLoadingOverlay(false); return; }
    const t = setTimeout(() => setShowLoadingOverlay(true), 250);
    return () => clearTimeout(t);
  }, [isLoading]);

  useEffect(() => {
    dispatch(setListViewState({
      stateKey,
      currentPage,
      shipmentsPerPage,
      sortBy,
      ascending,
      // Marks the tab as one the user has actually been on this session, which is
      // what tells a later mount to keep this position over the view's.
      visited: true,

    }));
  }, [dispatch, stateKey, currentPage, shipmentsPerPage, sortBy, ascending]);

  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenDropdownRow(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  useEffect(() => {
    const handle = (e) => {
      if (
        bulkMenuRef.current       && !bulkMenuRef.current.contains(e.target) &&
        checkboxHeaderRef.current && !checkboxHeaderRef.current.contains(e.target)
      ) setShowBulkMenu(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const disabledByShipment = React.useMemo(() => {
    const m = new Map();
    for (const s of shipments)
      m.set(makeKey(s.shipmentNumber, s.officeCode), DISABLED_STATUSES.has((s.status || "").toUpperCase()));
    return m;
  }, [shipments]);

  const fetchShipments = useCallback(async (signal) => {
    try {
      setIsLoading(true);
      const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
      if (officeCode) headers["Office-Code"] = officeCode;
      if (userId)     headers["userId"]      = userId.toString();

      const body = buildShipmentSearchPayload({
        searchQuery,
        filter,
        advancedFilters,
        sortBy,
        ascending,
        filterType,
        includeAdvancedFilters: enableAdvancedFilters,
      });

      const { data } = await httpClient.post(
        `${apiEndpoint}?page=${currentPage - 1}&size=${shipmentsPerPage}`,
        body,
        { headers, signal }
      );

      // A superseded request must not land: paging or filtering quickly leaves two
      // in flight, and the slower one answering last would put its rows — and its
      // shipment count, which the toolbar shows — back on screen.
      if (signal?.aborted) return;

      setShipments(data.content ?? []);
      // Every shipment this tab's filter matches, across all its pages.
      setTotalShipments(data.totalElements);
      setTotalItems(data.totalElements ?? 0);
      setTotalPages(data.totalPages);
    } catch (err) {
      if (signal?.aborted || err?.code === "ERR_CANCELED") return;
      console.error("Error fetching shipments:", err);
      setShipments([]);
      setTotalShipments(0);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [
    token, officeCode, userId, apiEndpoint,
    currentPage, shipmentsPerPage,
    searchQuery, filter, sortBy, ascending,
    advancedFilters, enableAdvancedFilters,
    filterType, setTotalShipments,
  ]);

  useEffect(() => {
    const filterChanged = prevFilterRef.current     !== filter;
    const searchChanged = prevSearchTrigRef.current !== searchTrigger;
    const needsReset    = (filterChanged || searchChanged) && currentPage !== 1;

    prevFilterRef.current     = filter;
    prevSearchTrigRef.current = searchTrigger;

    if (needsReset) { setCurrentPage(1); return undefined; }

    const controller = new AbortController();
    fetchShipments(controller.signal);
    return () => controller.abort();
  }, [searchTrigger, filter, currentPage, fetchShipments]);

  const handleSort = (newSortBy) => {
    setCurrentPage(1);
    if (sortBy !== newSortBy) { setSortBy(newSortBy); setAscending(true); }
    else setAscending((p) => !p);
    markDirty();
  };

  const formattedShipments = React.useMemo(
    () => shipments.map(formatShipmentRow),
    [shipments]
  );

  const handleSelectAll = (checked) => {
    if (!checked) { setSelectedShipments([]); return; }
    setSelectedShipments(
      formattedShipments
        .map((r) => makeKey(r["Shipment Number"], r["Office Code"]))
        .filter((key) => !disabledByShipment.get(key))
    );
  };

  const handleSingleCheckbox = (key) => {
    if (disabledByShipment.get(key)) return;
    setSelectedShipments((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // ── Bulk: Refresh Miles -- same as ShipmentsTable.js's; Drop Trailers are still Shipments,
  // just a filtered view of them. ──
  const handleBulkRefreshMiles = async () => {
    if (!selectedShipments.length) {
      notify.error({ title: "No Shipments Selected", message: "Please select at least one shipment." });
      setShowBulkMenu(false);
      return;
    }
    const shipmentNumbers = selectedShipments.map((k) => k.split("::")[0]);
    setIsRefreshingMiles(true);
    try {
      const { data } = await httpClient.post(
        `/api/shipments/refresh-transit-miles`,
        { shipmentNumbers },
        { headers: { Authorization: `Bearer ${token}`, "Office-Code": officeCode } }
      );
      const { totalFound, successfullyUpdated, failed, noChange, failures } = data;
      // One toast, styled to match what actually happened (see handleBulkRefreshRates for why).
      const summary = `Total: ${totalFound} | Updated: ${successfullyUpdated} | Unchanged: ${noChange} | Failed: ${failed}`;
      const details = (failures || []).map((f) => `${f.shipmentNumber} (${f.errorMessage})`).join("; ");
      if (failed === 0) {
        notify.success({ title: "Miles Refreshed", message: summary, options: { autoClose: 4000 } });
      } else if (successfullyUpdated === 0) {
        notify.error({ title: "Miles Refresh Failed", message: `${summary}\n${details}` });
      } else {
        notify.warning({ title: "Miles Partially Refreshed", message: `${summary}\n${details}` });
      }
      setSelectedShipments([]);
      fetchShipments();
    } catch (err) {
      notify.error({ title: "Refresh Failed", message: err?.response?.data?.message || "Could not refresh miles." });
    } finally {
      setIsRefreshingMiles(false);
      setShowBulkMenu(false);
    }
  };

  // ── Bulk: Refresh Rates -- same as ShipmentsTable.js's. No bulk backend endpoint exists for
  // this (unlike Refresh Miles), so this loops the same, already-proven single-shipment logic
  // (rateRefresh.js) across the selection in "silent" mode, aggregating one summary toast. ──
  const handleBulkRefreshRates = async () => {
    if (!selectedShipments.length) {
      notify.error({ title: "No Shipments Selected", message: "Please select at least one shipment." });
      setShowBulkMenu(false);
      return;
    }
    const shipmentEntries = selectedShipments.map((k) => {
      const [shipmentNumber, shipmentOfficeCode] = k.split("::");
      return { shipmentNumber, shipmentOfficeCode };
    });
    setIsRefreshingRates(true);
    const failures = [];
    const skipped = [];
    let successfullyUpdated = 0;
    try {
      for (const { shipmentNumber, shipmentOfficeCode } of shipmentEntries) {
        try {
          // detailed-shipment does an exact office-code match lookup -- a shipper admin
          // managing multiple offices can have selected shipments owned by different offices
          // in the same grid view, so this must use each row's OWN office code, not the
          // grid's single active-office cookie.
          const { data: fullShipment } = await httpClient.get(
            `/api/shipments/detailed-shipment/${shipmentNumber}`,
            { headers: { Authorization: `Bearer ${token}`, "Office-Code": shipmentOfficeCode || officeCode } }
          );

          // Rate-shopping inherently needs an assigned carrier to rate-shop against, and only
          // ever applies to Prepaid shipments. Neither of these applies to Refresh Miles (a
          // pure stop-to-stop distance calc), so they're checked here only.
          const hasCarrier = Boolean(
            fullShipment?.carrierOfficeCode?.partyOfficeCode || fullShipment?.carrierName
          );
          const isPrepaid = (fullShipment?.paymentMethod || "").toUpperCase() === "PREPAID";

          if (isRefreshBlockedForShipment(fullShipment)) {
            skipped.push({ shipmentNumber, reason: "Delivered/Completed and already invoiced" });
            continue;
          }
          if (!hasCarrier) {
            skipped.push({ shipmentNumber, reason: "No carrier assigned" });
            continue;
          }
          if (!isPrepaid) {
            skipped.push({ shipmentNumber, reason: "Only Prepaid shipments can be refreshed" });
            continue;
          }

          const result = await refreshRatesAndUpdateCharges({
            shipmentDetails: fullShipment,
            officeCode: shipmentOfficeCode || officeCode,
            isRefreshingRates: false,
            setIsRefreshingRates: () => {},
            silent: true,
          });
          if (result?.success) {
            successfullyUpdated += 1;
          } else {
            failures.push({ shipmentNumber, reason: result?.reason || "Refresh failed" });
          }
        } catch (err) {
          failures.push({
            shipmentNumber,
            reason: err?.response?.data?.message || "Could not fetch shipment details.",
          });
        }
      }
      const summary = `Total: ${shipmentEntries.length} | Updated: ${successfullyUpdated} | Skipped: ${skipped.length} | Failed: ${failures.length}`;
      const details = [...skipped, ...failures]
        .map((s) => `${s.shipmentNumber} (${s.reason})`)
        .join("; ");
      if (skipped.length === 0 && failures.length === 0) {
        notify.success({ title: "Rates Refreshed", message: summary, options: { autoClose: 4000 } });
      } else if (successfullyUpdated === 0) {
        notify.error({ title: "Rates Refresh Failed", message: `${summary}\n${details}` });
      } else {
        notify.warning({ title: "Rates Partially Refreshed", message: `${summary}\n${details}` });
      }
      setSelectedShipments([]);
      fetchShipments();
    } finally {
      setIsRefreshingRates(false);
      setShowBulkMenu(false);
    }
  };


  const handleViewShipment = (shipmentNumber, shipmentOfficeCode) => {
    dispatch(addTab({
      id:                `view-${shipmentNumber}-${Date.now()}`,
      name:              `${shipmentNumber}`,
      contentType:       "shipmentView",
      shipmentNumber,
      shipmentOfficeCode,
      isDynamic:         true,
      createdAt:         Date.now(),
    }));
  };

  const handleActionClick = (key, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenDropdownRow((prev) => (prev === key ? null : key));
  };

  const renderActionColumn = (row, disabled, getStickyCellProps) => {
    const key    = makeKey(row["Shipment Number"], row["Office Code"]);
    const id     = row["Shipment Number"];
    const shipOC = row["Office Code"];
    const isOpen = openDropdownRow === key;
    const { className: stickyClass, style: stickyStyle } = getStickyCellProps("Action");

    const actions = disabled
      ? [{ icon: "Eye",   label: "View",   color: "blue",  action: () => handleViewShipment(id, shipOC) }]
      : [
          { icon: "Eye",   label: "View",   color: "blue",  action: () => handleViewShipment(id, shipOC) },
          { icon: "Edit",  label: "Edit",   color: "green", action: () => {} },
          { icon: "Trash", label: "Delete", color: "red",   action: () => {} },
        ];

    return (
      <>
        <td
          onClick={(e) => e.stopPropagation()}
          className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${stickyClass}`}
          style={{
            ...stickyStyle,
            width: `${ACTION_COLUMN_WIDTH}px`, minWidth: `${ACTION_COLUMN_WIDTH}px`, maxWidth: `${ACTION_COLUMN_WIDTH}px`,
          }}
        >
          <button
            data-action-trigger
            className={`p-1 relative w-full transition-none ${
              disabled
                ? "opacity-60 hover:opacity-80"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text"
            }`}
            onClick={(e) => handleActionClick(key, e)}
          >
            <MoreHorizontal size={18} className="mx-auto" />
          </button>
        </td>

        {isOpen && ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
          >
            <ActionMenu
              rowId={key}
              isOpen
              onClose={() => setOpenDropdownRow(null)}
              moduleName="Shipment"
              actions={actions}
            />
          </div>,
          document.body
        )}
      </>
    );
  };

  const savedViewsControl = (
    <TableSavedViewsControl
      controlLabel={controlLabel}
      views={views}
      activeView={activeView}
      loading={viewsLoading}
      error={viewsError}
      isDirty={isDirty}
      allDataHeaders={TABLE_HEADERS.slice(2)}
      dataHeaders={dataHeaders}
      onSelectView={selectView}
      onSaveNew={saveNewView}
      onUpdateActive={updateActiveView}
      onRename={renameView}
      onDelete={deleteView}
      onSetDefault={setDefaultView}
      onUpdateColumnVisibility={updateColumnVisibility}
      onManageColumns={onManageColumns}
    />
  );

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">

      {enableAdvancedFilters ? (
        <AdvancedFilterToolbar
          filters={advancedFilters}
          activeCount={activeCount}
          onOpenModal={() => setShowFilterModal(true)}
          onClearAll={() => { clearFilters(); setCurrentPage(1); markDirty(); }}
          onRemovePill={(key) => { removePill(key); setCurrentPage(1); markDirty(); }}
          trailing={savedViewsControl}
        />
      ) : (
        <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
          {savedViewsControl}
        </div>
      )}

      <div
        ref={tableContainerRef}
        className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none relative"
      >
        <Table
          {...columnManagerProps}
          data={formattedShipments}
          initialColumnWidths={savedColumnWidths}
          onColumnResize={onColumnResize}
          onSelectAll={handleSelectAll}
          renderCheckboxActions={canRefreshMilesOrRates ? () => setShowBulkMenu((p) => !p) : undefined}
          checkboxHeaderRef={checkboxHeaderRef}
          selectedRows={selectedShipments}
          getRowSelectionKey={(row) => makeKey(row["Shipment Number"], row["Office Code"])}
          totalValidRows={formattedShipments.filter(
            (r) => !disabledByShipment.get(makeKey(r["Shipment Number"], r["Office Code"]))
          ).length}
          autoGrowColumns
          dataColumnMinWidth={DATA_COLUMN_MIN_WIDTH}
          dataColumnMaxWidth={DATA_COLUMN_MAX_WIDTH}
          enableColumnResize
          enableColumnReorder
          enableColumnPin
          nonHideableColumnIds={[SHIPMENT_NUMBER_HEADER]}
          sortBy={sortBy}
          ascending={ascending}
          onSort={(h) => { const k = SORTABLE_COLUMNS[h]; if (k) handleSort(k); }}
          sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
          sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
        >
          {(row, _rowIdx, columnWidths, getStickyCellProps, visibleColumns) => {
            const sn       = row["Shipment Number"];
            const key      = makeKey(sn, row["Office Code"]);
            const disabled = disabledByShipment.get(key);
            const { className: checkboxClass, style: checkboxStyle } = getStickyCellProps("Selection");
            const orderedDataHeaders = (visibleColumns ?? [])
              .map((col) => col.id)
              .filter((id) => id !== "Selection" && id !== "Action");
            const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;

            return (
              <>
                <td
                  className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${checkboxClass}`}
                  style={{
                    ...checkboxStyle,
                    width: `${SELECTION_COLUMN_WIDTH}px`, minWidth: `${SELECTION_COLUMN_WIDTH}px`, maxWidth: `${SELECTION_COLUMN_WIDTH}px`,
                  }}
                >
                  <input
                    type="checkbox"
                    className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                    checked={selectedShipments.includes(key)}
                    disabled={disabled}
                    onChange={() => handleSingleCheckbox(key)}
                  />
                </td>

                {renderActionColumn(row, disabled, getStickyCellProps)}

                {dataHeadersToRender.map((header) => {
                  const isExpanded = expandedRowKey === key;
                  const { className: colClass, style: colStyle } = getStickyCellProps(header);

                  return (
                    <td
                      key={header}
                      className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${colClass}`}
                      onDoubleClick={() => handleViewShipment(sn, row["Office Code"])}
                      {...getRowHoverProps(key)}
                      style={{
                        ...colStyle,
                        whiteSpace:   isExpanded ? "normal"   : "nowrap",
                        overflow:     "hidden",
                        textOverflow: "ellipsis",
                        // Lets unbroken tokens (IDs, comma-joined numbers) wrap too, not just space-separated text.
                        overflowWrap: "break-word",
                        wordBreak:    "break-word",

                        minWidth:     `${columnWidths?.[header] || DATA_COLUMN_MIN_WIDTH}px`,
                        maxWidth:     isExpanded ? `${DATA_COLUMN_MAX_WIDTH * 2}px` : `${DATA_COLUMN_MAX_WIDTH}px`,
                        width:        columnWidths?.[header] ? `${columnWidths[header]}px` : `${DATA_COLUMN_MIN_WIDTH}px`,
                        transition:   "max-width 120ms ease",
                      }}
                    >
                      {row[header]}
                    </td>
                  );
                })}
              </>
            );
          }}
        </Table>

        {showBulkMenu && canRefreshMilesOrRates &&
          (() => {
            const bulkMenuStyle = getBulkMenuStyle();
            if (!bulkMenuStyle) return null;

            return (
              <div
                ref={bulkMenuRef}
                className="absolute z-[1000]"
                style={bulkMenuStyle}
              >
                <ActionMenu
                  rowId="bulk" isOpen onClose={() => setShowBulkMenu(false)}
                  moduleName="Shipment" placement="header"
                  actions={[
                    {
                      icon: "RefreshCw",
                      label: isRefreshingMiles ? "Refreshing..." : "Refresh Miles",
                      color: "green",
                      action: handleBulkRefreshMiles,
                    },
                    {
                      icon: "RefreshCw",
                      label: isRefreshingRates ? "Refreshing..." : "Refresh Rates",
                      color: "green",
                      action: handleBulkRefreshRates,
                    },
                  ]}
                />
              </div>
            );
          })()}

        {showLoadingOverlay && (
          <div className="absolute inset-0 z-[2000] flex items-center justify-center bg-white/60 dark:bg-black/30">
            <Loader color="text-blue-600 dark:text-acs-dark-text" size="h-5 w-5" message={`Loading ${moduleName}...`} />
          </div>
        )}
      </div>

      <GlobalPagination
        label={paginationLabel}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={setCurrentPage}
        itemsPerPage={shipmentsPerPage}
        onItemsPerPageChange={setShipmentsPerPage}
      />

      {/* Advanced filter modal (rule-based: field + operator + value, joined by and/or) */}

      {enableAdvancedFilters && showFilterModal && (
        <AdvancedFilterModal
          filters={advancedFilters}
          quickFilterStatus={filter}
          statusOptions={advancedFilterStatusOptions}
          onApply={(f) => {
            updateFilters(f);
            // A status rule owns the status constraint — drop the quick filter.
            if (filtersOverrideStatus(f)) {
              dispatch(setStatusFilter(null));
              dispatch(setListViewState({ stateKey, filter: null }));
            }
            setCurrentPage(1);
            markDirty();
          }}
          onClose={() => setShowFilterModal(false)}
        />
      )}
    </div>
  );
};

export default PartialShipmentsTab;
